import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import type { MedicationStockReason } from '../types'
import {
  CURRENT_STOCK_QUERY_KEY,
  STOCK_LOGS_QUERY_KEY,
  UPDATE_STOCK_RECORD_URL
} from './queryConstants'

type UpdateStockRecordRequest = {
  notes?: string
  discrepancyReason?: string
  witnessIds?: string[]
  receivedBy?: string
  receivedFrom?: string
  disposedBy?: string
  disposalMethod?: string
  actionTime?: string
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

type UseUpdateStockRecordProps = {
  onSuccess?: () => void
  onError?: (error: any) => void
}

export const useUpdateStockRecord = ({
  onSuccess,
  onError
}: UseUpdateStockRecordProps = {}) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    ({
      recordId,
      data,
      type
    }: {
      recordId: string
      data: UpdateStockRecordRequest
      type: MedicationStockReason
    }) => {
      const params = getQueryParams({ type })
      const hasMedia = data.media && data.media.length > 0

      if (hasMedia && data.media) {
        const formData = new FormData()
        const { media, ...restData } = data

        Object.entries(restData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              if (key === 'witnessIds' && value.length === 0) {
                // Skip empty witnessIds array
              } else {
                formData.append(key, JSON.stringify(value))
              }
            } else {
              formData.append(key, String(value))
            }
          }
        })

        data.media.forEach((file) => {
          formData.append('media', file)
        })

        return postJSON({
          url: `${UPDATE_STOCK_RECORD_URL}/${recordId}?${params}`,
          method: 'PATCH',
          contentType: 'multipart/form-data',
          body: formData
        })
      }

      const { media, ...jsonData } = data
      return postJSON({
        url: `${UPDATE_STOCK_RECORD_URL}/${recordId}?${params}`,
        method: 'PATCH',
        json: jsonData
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error updating stock record:', error)
        onError?.(error)
      }
    }
  )

  return {
    updateStockRecord: mutation.mutateAsync,
    isLoading: mutation.isLoading
  }
}

export default useUpdateStockRecord
